import React from 'react';
import { formatDate } from './utils/dateFormatter';
import {
	Modal,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Input,
	Select,
	SelectItem,
	Checkbox,
	RadioGroup,
	Radio,
	addToast
} from '@heroui/react';
import { Copy } from 'lucide-react';
import { AppDatePicker } from './components/AppDatePicker';
import { INSPECTION_TYPES, PRESET_HINTS } from './InspectionsPage.js';

/**
 * Shared type-specific form fields for Add/Update inspection modals.
 * Renders date picker, frequency, meter selection, "Inspection by Meter" label,
 * due date/meter inputs, and counter value inputs based on inspection type.
 */
function TypeSpecificFields({ formData, handleFormChange, counters }) {
	const { type } = formData;

	return (
		<>
			{/* Issue Date - for all types */}
			<AppDatePicker
				label="Inspection Date"
				value={formData.issueDate}
				onChange={value => handleFormChange('issueDate', value)}
			/>

			{/* Calendar type - frequency in months */}
			{type === 'calendar' && (
				<Input
					type="number"
					label="Frequency (months)"
					placeholder="e.g., 12 for annual"
					value={formData.frequency}
					onChange={e => handleFormChange('frequency', e.target.value)}
					isRequired={formData.preset === 'custom'}
					isDisabled={formData.preset !== 'custom'}
				/>
			)}

			{/* Meter type - frequency in hours + meter selection */}
			{type === 'meter' && (
				<>
					<Input
						type="number"
						label="Frequency (hours)"
						placeholder="e.g., 100"
						value={formData.frequency}
						onChange={e => handleFormChange('frequency', e.target.value)}
						isRequired={formData.preset === 'custom'}
						isDisabled={formData.preset !== 'custom'}
					/>
					<Select
						label="Active Meter"
						description="Meter used for tracking this inspection"
						placeholder="Select meter"
						selectedKeys={formData.activeCounter ? [formData.activeCounter] : []}
						onSelectionChange={keys => handleFormChange('activeCounter', Array.from(keys)[0] || '')}
						isRequired
					>
						{counters.map(counter => (
							<SelectItem key={String(counter.id)}>{counter.name}</SelectItem>
						))}
					</Select>
					{formData.activeCounter && (
						<div>
							<p className="text-sm font-bold">Inspection by Meter</p>
							<p className="text-sm text-default-600">
								{counters.find(c => String(c.id) === String(formData.activeCounter))?.name || `Counter #${formData.activeCounter}`}
							</p>
						</div>
					)}
				</>
			)}

			{/* Due Date type */}
			{type === 'dueDate' && (
				<AppDatePicker
					label="Due Date"
					value={formData.dueDate}
					onChange={value => handleFormChange('dueDate', value)}
					isRequired
				/>
			)}

			{/* Due Meter type */}
			{type === 'dueMeter' && (
				<>
					<Select
						label="Meter"
						placeholder="Select meter"
						selectedKeys={formData.activeCounter ? [formData.activeCounter] : []}
						onSelectionChange={keys => handleFormChange('activeCounter', Array.from(keys)[0] || '')}
						isRequired
					>
						{counters.map(counter => (
							<SelectItem key={String(counter.id)}>{counter.name}</SelectItem>
						))}
					</Select>
					{formData.activeCounter && (
						<div>
							<p className="text-sm font-bold">Inspection by Meter</p>
							<p className="text-sm text-default-600">
								{counters.find(c => String(c.id) === String(formData.activeCounter))?.name || `Counter #${formData.activeCounter}`}
							</p>
						</div>
					)}
					<Input
						type="number"
						label="Due at Meter Value"
						placeholder="e.g., 1500.0"
						value={formData.counterExpirationValue}
						onChange={e => handleFormChange('counterExpirationValue', e.target.value)}
						isRequired
					/>
				</>
			)}

			{/* Inspection Meter Values - shown for ALL inspection types */}
			{counters.length > 0 && (
				<div className="space-y-3 pt-2 border-t border-default-200">
					<p className="text-sm font-medium text-default-700">Inspection Meter Values</p>
					{counters.map(counter => (
						<Input
							key={counter.id}
							type="number"
							label={counter.name}
							placeholder="e.g., 1234.5"
							description={`Current: ${counter.value || 0}`}
							value={formData.countersValue[counter.id] || ''}
							onChange={e => handleFormChange('countersValue', {
								...formData.countersValue,
								[counter.id]: e.target.value
							})}
						/>
					))}
				</div>
			)}
		</>
	);
}

export function AddInspectionModal({
	isOpen,
	onClose,
	formData,
	saving,
	inspectionPresets,
	counters,
	handlePresetChange,
	handleFormChange,
	handleAddInspection
}) {
	return (
		<Modal scrollBehavior="inside" isOpen={isOpen} onClose={onClose} size="lg">
			<ModalContent>
				{onClose => (
					<>
						<ModalHeader>Add Inspection</ModalHeader>
						<ModalBody>
							<div className="space-y-4">
								{/* Preset Selection */}
								<Select
									label="Inspection Type"
									placeholder="Select inspection type"
									selectedKeys={formData.preset ? [formData.preset] : []}
									onSelectionChange={keys => handlePresetChange(Array.from(keys)[0] || '')}
								>
									{inspectionPresets.map(preset => (
										<SelectItem key={preset.unique} description={preset.description}>
											{preset.inspectionName}
										</SelectItem>
									))}
								</Select>

								{/* Show additional fields only when preset is selected */}
								{formData.preset && (
									<>
										{/* Custom type selection - only for custom preset */}
										{formData.preset === 'custom' && (
											<Select
												label="Tracking Method"
												selectedKeys={[formData.type]}
												onSelectionChange={keys => handleFormChange('type', Array.from(keys)[0] || 'calendar')}
											>
												{INSPECTION_TYPES.map(type => (
													<SelectItem key={type.value} description={type.description}>
														{type.label}
													</SelectItem>
												))}
											</Select>
										)}

										{/* Inspection Name */}
										<Input
											label="Inspection Name"
											placeholder="Enter inspection name"
											value={formData.inspectionName}
											onChange={e => handleFormChange('inspectionName', e.target.value)}
											isRequired
											isDisabled={formData.preset !== 'custom'}
										/>

										{/* Affects Airworthiness */}
										<Checkbox
											isSelected={formData.affectsAirworthiness}
											onValueChange={val => handleFormChange('affectsAirworthiness', val)}
										>
											Affects Airworthiness
										</Checkbox>

										{/* Type-specific fields */}
										<TypeSpecificFields formData={formData} handleFormChange={handleFormChange} counters={counters} />
									</>
								)}
							</div>
						</ModalBody>
						<ModalFooter>
							<Button variant="light" onPress={onClose} isDisabled={saving}>
								Cancel
							</Button>
							<Button
								color="primary"
								onPress={handleAddInspection}
								isLoading={saving}
								isDisabled={!formData.preset}
							>
								Add Inspection
							</Button>
						</ModalFooter>
					</>
				)}
			</ModalContent>
		</Modal>
	);
}

export function UpdateInspectionModal({
	isOpen,
	onClose,
	formData,
	saving,
	inspectionPresets,
	counters,
	handleFormChange,
	handleRenewInspection
}) {
	return (
		<Modal scrollBehavior="inside" isOpen={isOpen} onClose={onClose} size={(formData.type === 'meter' || formData.type === 'dueMeter') && counters.length > 0 ? '4xl' : '2xl'}>
			<ModalContent>
				{onClose => {
					const isCustom = formData.preset === 'custom';
					const isMeterType = formData.type === 'meter' || formData.type === 'dueMeter';
					const showMeterInputs = isMeterType;
					const frequencyUnit = isMeterType ? 'hours' : 'months';
					return (
					<>
						<ModalHeader>Update Inspection</ModalHeader>
						<ModalBody>
							<div className={`grid grid-cols-1 gap-6 ${showMeterInputs && counters.length > 0 ? 'md:grid-cols-[1fr_1.5fr_1.5fr]' : 'md:grid-cols-[1fr_2fr]'}`}>
								{/* Left Column — Type & Airworthiness */}
								<div className="space-y-4 md:border-r md:border-default-200 md:pr-6">
									<Checkbox
										isSelected={formData.affectsAirworthiness}
										onValueChange={val => handleFormChange('affectsAirworthiness', val)}
										size="sm"
									>
										Affects Airworthiness
									</Checkbox>

									<div>
										<p className="text-sm font-bold mb-1">Inspection Type</p>
										<div className="bg-default-100 rounded-lg px-3 py-2 text-sm text-default-600">
											{isCustom ? 'Custom' : (inspectionPresets.find(p => p.unique === formData.preset)?.inspectionName || formData.preset || 'Unknown')}
										</div>
									</div>

									{isCustom && (
										<RadioGroup
											value={formData.type}
											isDisabled
											size="sm"
										>
											{INSPECTION_TYPES.map(t => (
												<Radio key={t.value} value={t.value}>{t.label}</Radio>
											))}
										</RadioGroup>
									)}

									{!isCustom && PRESET_HINTS[formData.preset] && (
										<p className="text-sm text-default-500">{PRESET_HINTS[formData.preset]}</p>
									)}
								</div>

								{/* Middle Column — Inspection Details */}
								<div className="space-y-4">
									<p className="text-base font-semibold text-default-700">Inspection Details</p>

									<Input
										label="Inspection Name"
										value={formData.inspectionName}
										onChange={e => handleFormChange('inspectionName', e.target.value)}
										isRequired
									/>

									<div>
										<AppDatePicker
											label="Enter Inspection Date"
											value={formData.issueDate}
											onChange={value => handleFormChange('issueDate', value)}
											isRequired={formData.type === 'calendar'}
										/>
										{formData.issueDate && (
											<p className="text-xs text-default-400 px-1" style={{marginTop: '6px'}}>{formatDate(formData.issueDate.toString())}</p>
										)}
									</div>

									{/* Frequency — editable for custom (calendar/meter only), read-only for presets */}
									{isCustom ? (
										(formData.type === 'calendar' || formData.type === 'meter') && (
											<div>
												<Input
													type="number"
													label="Frequency"
													value={formData.frequency}
													onChange={e => handleFormChange('frequency', e.target.value)}
													isRequired
												/>
												<p className="text-xs text-default-400 px-1" style={{marginTop: '6px'}}>{frequencyUnit}</p>
											</div>
										)
									) : formData.frequency ? (
										<div>
											<p className="text-sm font-bold">Frequency</p>
											<p className="text-sm text-default-600">{formData.frequency} {frequencyUnit}</p>
										</div>
									) : null}

									{/* Inspection by Meter — shown for all meter-based inspections (standard + custom) */}
									{isMeterType && formData.activeCounter && (
										<div>
											<p className="text-sm font-bold">Inspection by Meter</p>
											<p className="text-sm text-default-600">
												{counters.find(c => String(c.id) === String(formData.activeCounter))?.name || `Counter #${formData.activeCounter}`}
											</p>
										</div>
									)}

									{/* Type-specific fields */}
									{formData.type === 'dueDate' && (
										<div>
											<AppDatePicker
												label="New Due Date"
												value={formData.dueDate}
												onChange={value => handleFormChange('dueDate', value)}
												isRequired
											/>
											{formData.dueDate && (
												<p className="text-xs text-default-400 px-1" style={{marginTop: '6px'}}>{formatDate(formData.dueDate.toString())}</p>
											)}
										</div>
									)}

									{formData.type === 'dueMeter' && (
										<Input
											type="number"
											label="New Due Meter Value"
											value={formData.counterExpirationValue}
											onChange={e => handleFormChange('counterExpirationValue', e.target.value)}
											isRequired
											isDisabled={!formData.activeCounter}
											step="0.01"
											max="9999999.99"
										/>
									)}
								</div>

								{/* Right Column — Inspection Meter Values (hidden when no counters or no meter inputs) */}
								{showMeterInputs && counters.length > 0 && (
									<div className="space-y-4">
										<p className="text-base font-semibold text-default-700">Inspection Meter Values</p>

										{showMeterInputs ? (
											counters.map(counter => (
												<div key={counter.id}>
													<Input
														type="number"
														label={counter.name}
														placeholder="e.g., 1234.5"
														value={formData.countersValue[counter.id] || ''}
														onChange={e => handleFormChange('countersValue', {
															...formData.countersValue,
															[counter.id]: e.target.value
														})}
														size="sm"
														step="0.01"
														max="9999999.99"
														endContent={
															<button
																type="button"
																className="text-default-400 hover:text-default-600"
																title="Insert current meter value"
																onClick={() => {
																	const currentValue = counter.value ?? 0;
																	handleFormChange('countersValue', {
																		...formData.countersValue,
																		[counter.id]: String(currentValue)
																	});
																	addToast({ title: 'Inserted', description: `${counter.name}: ${currentValue}`, color: 'success' });
																}}
															>
																<Copy className="w-4 h-4" />
															</button>
														}
													/>
													<p className="text-xs text-default-400 px-1" style={{marginTop: '6px'}}>(Current Value: {counter.value ?? 0})</p>
												</div>
											))
										) : null}
									</div>
								)}
							</div>
						</ModalBody>
						<ModalFooter>
							<Button variant="light" onPress={onClose} isDisabled={saving}>
								Cancel
							</Button>
							<Button color="primary" onPress={handleRenewInspection} isLoading={saving}>
								Update Inspection
							</Button>
						</ModalFooter>
					</>
					);
				}}
			</ModalContent>
		</Modal>
	);
}
